/** @format */

import React, { useState, useMemo, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaChevronDown, FaChevronRight } from "react-icons/fa";
import Sidebar_Logo from "../../assets/logo_animation.png";
import Sidebar_Logo_col from "../../assets/HRMS LOGO CON-01.png";
import sidebarData from "./sidebar_data";
import { FiLogOut } from "react-icons/fi";
import usePermissions from "../permissions/permission";

const Sidebar = ({ isOpen = true, toggleSidebar = () => {} }) => {
  const [expandedMenu, setExpandedMenu] = useState({});
  const [isShaking, setIsShaking] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Safely get permissions with fallback
  const permissionsHook = usePermissions();
  const {
    hasPermission = () => false,
    permissions = [],
    isLoading = false,
  } = permissionsHook || {};

  // Filter sidebar data based on user permissions
  const filteredSidebarData = useMemo(() => {
    try {
      if (isLoading || !Array.isArray(permissions) || !sidebarData) {
        return [];
      }

      return sidebarData
        .filter((module) => {
          try {
            if (!module || typeof module !== "object") return false;

            // Check if user has permission for main module
            const hasMainPermission = Array.isArray(
              module.requiredPermissionsformainfeatures
            )
              ? module.requiredPermissionsformainfeatures.some(
                  (permission) => hasPermission && hasPermission(permission)
                )
              : true; // Show if no permission requirement

            if (!hasMainPermission) {
              return false;
            }

            // If module has submodules, filter them based on permissions
            if (module.subModules && Array.isArray(module.subModules)) {
              const filteredSubModules = module.subModules.filter(
                (subModule) => {
                  try {
                    if (!subModule || typeof subModule !== "object")
                      return false;

                    if (
                      !Array.isArray(
                        subModule.requiredPermissionsforsubfeatures
                      )
                    ) {
                      return true; // Show submodule if no permission requirement
                    }

                    return subModule.requiredPermissionsforsubfeatures.some(
                      (permission) => hasPermission && hasPermission(permission)
                    );
                  } catch (error) {
                    console.error("Error filtering submodule:", error);
                    return false;
                  }
                }
              );

              // Only show module if it has at least one accessible submodule
              return filteredSubModules.length > 0;
            }

            return true; // Show module if no submodules or main permission check passed
          } catch (error) {
            console.error("Error filtering module:", error);
            return false;
          }
        })
        .map((module) => {
          try {
            // Apply submodule filtering
            if (module.subModules && Array.isArray(module.subModules)) {
              const filteredSubModules = module.subModules.filter(
                (subModule) => {
                  try {
                    if (!subModule || typeof subModule !== "object")
                      return false;

                    if (
                      !Array.isArray(
                        subModule.requiredPermissionsforsubfeatures
                      )
                    ) {
                      return true;
                    }

                    return subModule.requiredPermissionsforsubfeatures.some(
                      (permission) => hasPermission && hasPermission(permission)
                    );
                  } catch (error) {
                    console.error("Error filtering submodule in map:", error);
                    return false;
                  }
                }
              );

              return {
                ...module,
                subModules: filteredSubModules,
              };
            }

            return module;
          } catch (error) {
            console.error("Error mapping module:", error);
            return module;
          }
        });
    } catch (error) {
      console.error("Error in filteredSidebarData:", error);
      return [];
    }
  }, [permissions, hasPermission, isLoading]);

  const toggleSubMenu = useCallback((name) => {
    try {
      if (name && typeof name === "string") {
        setExpandedMenu((prev) => ({ ...prev, [name]: !prev[name] }));
      }
    } catch (error) {
      console.error("Error toggling submenu:", error);
    }
  }, []);

  const isSelectedPath = useCallback(
    (path) => {
      try {
        if (!path || !location?.pathname) return false;
        return (
          location.pathname === path || location.pathname.startsWith(`${path}/`)
        );
      } catch (error) {
        console.error("Error checking selected path:", error);
        return false;
      }
    },
    [location?.pathname]
  );

  const renderSubModules = useCallback(
    (subModules, parentPath = "") => {
      try {
        if (!Array.isArray(subModules)) return null;

        return (
          <ul className={`ml-4 ${!isOpen && "hidden"}`}>
            {subModules.map((subModule) => {
              try {
                if (!subModule || !subModule._id) return null;

                const currentPath = `${parentPath}${subModule.url || ""}`;
                const isSelected = isSelectedPath(currentPath);
                return (
                  <li
                    key={subModule._id}
                    className={`flex items-center p-2 text-sm ${
                      isSelected ? "font-bold text-white" : "text-gray-200"
                    }`}
                  >
                    {isSelected && (
                      <span className="mr-2 w-2 h-2 bg-white rounded-full"></span>
                    )}
                    <Link to={currentPath}>{subModule.name || "Unnamed"}</Link>
                  </li>
                );
              } catch (error) {
                console.error("Error rendering submodule:", error);
                return null;
              }
            })}
          </ul>
        );
      } catch (error) {
        console.error("Error rendering submodules:", error);
        return null;
      }
    },
    [isOpen, isSelectedPath]
  );

  const handleLogout = useCallback(() => {
    try {
      setShowLogoutConfirm(true);
    } catch (error) {
      console.error("Error handling logout:", error);
    }
  }, []);

  const confirmLogout = useCallback(() => {
    try {
      // Clear all localStorage and cookies
      localStorage.clear();

      // Clear all cookies
      document.cookie.split(";").forEach((c) => {
        document.cookie = c
          .replace(/^ +/, "")
          .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });

      if (navigate) {
        navigate("/login");
      }
    } catch (error) {
      console.error("Error during logout:", error);
      // Fallback: redirect manually
      window.location.href = "/login";
    }
  }, [navigate]);

  const handleMouseEnter = useCallback(() => {
    try {
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
      if (!isOpen && toggleSidebar) {
        toggleSidebar();
      }
    } catch (error) {
      console.error("Error in mouse enter:", error);
    }
  }, [isOpen, toggleSidebar]);

  const handleMouseLeave = useCallback(() => {
    try {
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
      if (isOpen && toggleSidebar) {
        toggleSidebar();
      }
    } catch (error) {
      console.error("Error in mouse leave:", error);
    }
  }, [isOpen, toggleSidebar]);

  // Safe navigation handler
  const handleNavigation = useCallback(
    (url, hasSubModules, moduleId) => {
      try {
        if (hasSubModules) {
          toggleSubMenu(moduleId);
        } else if (url && navigate) {
          navigate(url);
        }
      } catch (error) {
        console.error("Error in navigation:", error);
      }
    },
    [toggleSubMenu, navigate]
  );

  return (
    <>
      <div
        className={`fixed top-0 left-0 h-screen bg-[#001F3F] p-5 pt-8 shadow-lg transition-all duration-500 ease-in-out ${
          isOpen ? "w-64" : "w-20"
        } flex flex-col ${isShaking ? "animate-shakeX" : ""}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="text-center text-white mb-12">
          {isOpen ? (
            <img src={Sidebar_Logo} alt="Logo" className="mx-auto w-30 h-30" />
          ) : (
            <img
              src={Sidebar_Logo_col}
              alt="Collapsed Logo"
              className="mx-auto w-[65px] h-[40px]"
            />
          )}
        </div>

        <div className="overflow-y-auto flex-1 custom-scrollbar">
          {isLoading ? (
            <div className="text-center text-white p-4">
              {isOpen && <span>Loading...</span>}
            </div>
          ) : (
            <ul>
              {Array.isArray(filteredSidebarData) &&
                filteredSidebarData.map((module) => {
                  try {
                    if (!module || !module._id) return null;

                    const isModuleSelected = isSelectedPath(module.url);
                    const hasSubModules =
                      module.subModules &&
                      Array.isArray(module.subModules) &&
                      module.subModules.length > 0;

                    return (
                      <li key={module._id} className="mt-1">
                        <div
                          className={`flex items-center p-2 rounded-md cursor-pointer transition-colors duration-300 ease-in-out ${
                            isModuleSelected
                              ? "bg-[#2495FE]"
                              : "hover:bg-[#01E6FF]"
                          } text-white`}
                          onClick={() =>
                            handleNavigation(
                              module.url,
                              hasSubModules,
                              module._id
                            )
                          }
                        >
                          {module.icon && (
                            <span
                              className={`transition-transform duration-300 ${
                                isOpen ? "scale-100" : "scale-75"
                              }`}
                            >
                              {module.icon}
                            </span>
                          )}
                          {isOpen && (
                            <span className="ml-2">
                              {module.name || "Unnamed"}
                            </span>
                          )}
                          {isOpen && hasSubModules && (
                            <span className="ml-auto">
                              {expandedMenu[module._id] ? (
                                <FaChevronDown />
                              ) : (
                                <FaChevronRight />
                              )}
                            </span>
                          )}
                        </div>
                        {expandedMenu[module._id] &&
                          hasSubModules &&
                          renderSubModules(module.subModules, module.url)}
                      </li>
                    );
                  } catch (error) {
                    console.error("Error rendering module:", error);
                    return null;
                  }
                })}
            </ul>
          )}
        </div>

        {/* Logout Button */}
        <div className="mt-auto border-t border-gray-600 pt-4">
          <div
            className="flex items-center p-2 rounded-md cursor-pointer transition-colors duration-300 ease-in-out hover:bg-red-600 text-white"
            onClick={handleLogout}
          >
            <FiLogOut
              className={`transition-transform duration-300 ${
                isOpen ? "scale-100" : "scale-75"
              }`}
            />
            {isOpen && <span className="ml-2">Logout</span>}
          </div>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-80 text-center shadow-lg">
            <h2 className="text-lg font-semibold mb-4">Confirm Logout</h2>
            <p className="mb-6">Are you sure you want to logout?</p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => {
                  try {
                    setShowLogoutConfirm(false);
                  } catch (error) {
                    console.error("Error canceling logout:", error);
                  }
                }}
                className="px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={confirmLogout}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Yes, Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Error boundary wrapper for the sidebar
const SidebarWithErrorBoundary = (props) => {
  try {
    return <Sidebar {...props} />;
  } catch (error) {
    console.error("Sidebar error:", error);
    return (
      <div className="fixed top-0 left-0 h-screen bg-[#001F3F] w-20 p-5 pt-8 shadow-lg">
        <div className="text-center text-white text-sm">
          Error loading sidebar
        </div>
      </div>
    );
  }
};

export default SidebarWithErrorBoundary;
